import { Config } from '../../video-data/config';
import type { Asset, BaseAsset } from '../asset/BaseAsset';
import type { AssetGroup } from '../asset/AssetGroup';
import type { Scene } from '../scene/Scene';

export type MorphTechnique = 'true-morph' | 'match-cut';

export type MorphPairing = {
    sourceId: string;
    targetId: string;
    technique: MorphTechnique;
};

export type SceneBoundary = 'end' | 'start';

export type ResolvedMatchCutPair = { sourceAsset: Asset; targetAsset: Asset };

function getRootAssetGroup(asset: Asset): AssetGroup | undefined {
    const climbToRoot = (group: AssetGroup | undefined): AssetGroup | undefined => {
        const parentGroup = group?.getParentByType(Config.GROUP_ASSET_TYPE);
        return parentGroup ? climbToRoot(parentGroup) : group;
    };
    return climbToRoot(asset.getParentByType(Config.GROUP_ASSET_TYPE));
}

function isMorphableAssetType(asset: Asset): boolean {
    return (
        Config.isTextAsset(asset) ||
        Config.isMediaAsset(asset) ||
        Config.isIconAsset(asset) ||
        Config.isLottieAsset(asset) ||
        Config.isAssetGroup(asset) ||
        Config.isCircleAsset(asset) ||
        Config.isRectangleAsset(asset) ||
        Config.isGradientAsset(asset)
    );
}

export function assetsAtBoundary(scene: Scene, boundary: SceneBoundary): Asset[] {
    return boundary === 'end' ? scene.getAssetsAtSceneEnd() : scene.getAssetsAtSceneStart();
}

function getMorphableAssets(scene: Scene, boundary: SceneBoundary): Asset[] {
    // Exclude stickers/duplicates — in a morph chain, scene N's duplicates would otherwise short-circuit N+1's pairing.
    return assetsAtBoundary(scene, boundary).filter(
        (asset) =>
            !asset.isMatchCutDuplicate &&
            !Config.isStickerAsset(asset) &&
            isMorphableAssetType(asset),
    );
}

// Bail if a native morph already animates these assets (same-uuid image/icon, identical text); empty text ignored.
function sharesNativelyMorphingAsset(sources: Asset[], targets: Asset[]): boolean {
    const rendersVisibleContent = (asset: Asset) => !Config.isTextAsset(asset) || asset.hasText;
    const visibleTargets = targets.filter(rendersVisibleContent);
    return sources
        .filter(rendersVisibleContent)
        .some((source) => visibleTargets.some((target) => source.isMorphMatch(target)));
}

// Uses getBounds, not transformation.position — laid-out text sits at position (0,0) with its real position from layout.
export function visualCenter(asset: Asset): { x: number; y: number } {
    const bounds = asset.getBounds();
    return { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
}

function distanceBetween(a: Asset, b: Asset): number {
    const centerA = visualCenter(a);
    const centerB = visualCenter(b);
    return Math.hypot(centerA.x - centerB.x, centerA.y - centerB.y);
}

// Match sources in reading order to the nearest remaining target (paths can cross); count-mismatch leftovers freeze.
function nearestNeighbourPairs(
    sources: Asset[],
    targets: Asset[],
): { source: Asset; target: Asset }[] {
    const readingOrder = (a: Asset, b: Asset) => {
        const centerA = visualCenter(a);
        const centerB = visualCenter(b);
        return centerA.y - centerB.y || centerA.x - centerB.x;
    };

    const availableTargets = [...targets];
    const pairs: { source: Asset; target: Asset }[] = [];

    for (const source of [...sources].sort(readingOrder)) {
        if (availableTargets.length === 0) break;
        const nearest = availableTargets.reduce(
            (best, target, index) => {
                const distance = distanceBetween(source, target);
                return distance < best.distance ? { index, distance } : best;
            },
            { index: 0, distance: Infinity },
        );
        pairs.push({ source, target: availableTargets[nearest.index] });
        availableTargets.splice(nearest.index, 1);
    }

    return pairs;
}

// Morphs the top-most enclosing group, not the bare text leaf — a grouped leaf's asset.x is 0 (layout-driven) and would fly off.
export function getTextMorphUnits(scene: Scene, boundary: SceneBoundary): Asset[] {
    const units = new Set<Asset>();
    assetsAtBoundary(scene, boundary)
        // Exclude stickers/duplicates so a rerun doesn't re-pair already-created match-cut duplicates.
        .filter(
            (asset) =>
                Config.isTextAsset(asset) &&
                asset.hasText &&
                !asset.isMatchCutDuplicate &&
                !Config.isStickerAsset(asset),
        )
        .forEach((leaf) => units.add(getRootAssetGroup(leaf) ?? leaf));
    return [...units];
}

function textMorphPairings(sourceScene: Scene, targetScene: Scene): MorphPairing[] {
    const sources = getTextMorphUnits(sourceScene, 'end');
    const targets = getTextMorphUnits(targetScene, 'start');
    if (sources.length === 0 || targets.length === 0) {
        return [];
    }
    return nearestNeighbourPairs(sources, targets).map(({ source, target }) => ({
        sourceId: source.id,
        targetId: target.id,
        technique: source.isMorphMatch(target) ? 'true-morph' : 'match-cut',
    }));
}

// Re-derive pairings from the current layout — pairing only (no scoring), so scoring can't restyle a saved match-cut.
export function deriveMatchCutPairings(sourceScene: Scene, targetScene: Scene): MorphPairing[] {
    const sources = getMorphableAssets(sourceScene, 'end');
    const targets = getMorphableAssets(targetScene, 'start');
    if (sources.length === 0 || targets.length === 0) {
        return [];
    }
    if (sharesNativelyMorphingAsset(sources, targets)) {
        return [];
    }
    return textMorphPairings(sourceScene, targetScene);
}

function canDuplicateAssetForMatchCut(asset: Asset): boolean {
    return (
        Config.isAssetGroup(asset) ||
        Config.isTextAsset(asset) ||
        Config.isFancyCaptionAsset(asset) ||
        Config.isMediaAsset(asset)
    );
}

function isAssetNestedWithin(asset: BaseAsset, ancestorIds: Set<string>): boolean {
    const parent = asset.parent;
    if (!parent) {
        return false;
    }
    if (ancestorIds.has(parent.id)) {
        return true;
    }
    return isAssetNestedWithin(parent, ancestorIds);
}

// Resolve pairings to live assets, dropping unduplicatable ones and those nested inside another pair.
export function resolveMatchCutPairs(
    scene: Scene,
    nextScene: Scene,
    pairings: MorphPairing[],
): ResolvedMatchCutPair[] {
    const matchCutPairings = pairings.filter((pairing) => pairing.technique === 'match-cut');
    const sourceIds = new Set(matchCutPairings.map((pairing) => pairing.sourceId));
    const targetIds = new Set(matchCutPairings.map((pairing) => pairing.targetId));

    return matchCutPairings.flatMap((pairing) => {
        const sourceAsset = scene.getAssetById(pairing.sourceId, { deep: true });
        const targetAsset = nextScene.getAssetById(pairing.targetId, { deep: true });
        if (!sourceAsset || !targetAsset) {
            return [];
        }
        if (
            !canDuplicateAssetForMatchCut(sourceAsset) ||
            !canDuplicateAssetForMatchCut(targetAsset)
        ) {
            return [];
        }
        if (
            isAssetNestedWithin(sourceAsset, sourceIds) ||
            isAssetNestedWithin(targetAsset, targetIds)
        ) {
            return [];
        }
        return [{ sourceAsset, targetAsset }];
    });
}
